/**
 * 图片尺寸（DESIGN.md §6.5 补充：「图像→图像大小」，参照 Photoshop）。
 *
 * 跟"画布大小"（`app/canvas-size.ts`）是两件不同的事：画布大小只改边界、
 * 内容原样不动；这里是把**整张画布的内容**缩放到新分辨率——对每个
 * raster 图层（含蒙版）复用"自由变换"提交时用的同一个烘焙原语
 * `bakeLayerTransform`（wasm Lanczos-3/双线性重采样，跟自由变换视觉
 * 结果一致），矩阵是一个从原点出发的纯缩放（`scaling(sx, sy)`）。
 *
 * **已知限制**：形状（`path` 是 SVG 路径字符串）、文字（`box`+`fontSize`
 * 在图层局部空间）、矢量蒙版（`vectorMask`，同样是路径字符串）目前没有
 * 跟着缩放——这个仓库里没有 SVG 路径坐标的解析/重写工具，新写一个属于
 * 独立的活，不是"图片尺寸"这一步能顺手带上的（跟 ExportDialog 头注释
 * "GIF 没做"是同一类诚实披露，不是伪造完成）。返回值里的
 * `skippedVectorLayers` 数出这类图层，装配层据此提示用户。
 * 智能对象（`smartObject`）出于同一个理由（其 `raster` 的存储 id 没有
 * 现成的重采样先例可以确认）也先跳过，计入同一个计数。
 */
import { scaling, type Mat3 } from '../engine/transform/mat3';
import { bakeLayerTransform, type BakeOptions } from '../engine/transform/bake';
import { maskLayerIdFor } from './commands/mask';
import { createSetCanvasSizeCommand } from './commands/canvas-commands';
import type { Layer } from '../document/types';
import type { TileStore } from '../engine/tile/store';
import type { StrokeTransaction } from '../engine/brush/buffer';
import type { AppStore } from './store';

async function resizeLayers(
  tiles: TileStore,
  layers: readonly Layer[],
  matrix: Mat3,
  newWidth: number,
  newHeight: number,
  transaction: StrokeTransaction,
  dirty: Array<{ tx: number; ty: number }>,
  options: BakeOptions,
): Promise<number> {
  let skipped = 0;
  for (const layer of layers) {
    if (layer.mask !== undefined) {
      const result = await bakeLayerTransform(
        tiles,
        maskLayerIdFor(layer.id),
        layer.mask.bounds,
        matrix,
        newWidth,
        newHeight,
        transaction,
        options,
      );
      dirty.push(...result.dirty);
    }
    if (layer.vectorMask !== undefined) skipped++;

    if (layer.kind === 'group') {
      skipped += await resizeLayers(tiles, layer.children, matrix, newWidth, newHeight, transaction, dirty, options);
    } else if (layer.kind === 'raster') {
      const result = await bakeLayerTransform(tiles, layer.id, layer.bounds, matrix, newWidth, newHeight, transaction, options);
      dirty.push(...result.dirty);
    } else if (layer.kind === 'shape' || layer.kind === 'text' || layer.kind === 'smartObject') {
      skipped++;
    }
    // fill/adjustment 图层没有尺寸相关的几何字段，天然适配任何画布大小，不用管
  }
  return skipped;
}

export interface ResizeImageResult {
  readonly resized: boolean;
  /** 没跟着缩放的图层数（形状/文字/矢量蒙版/智能对象），0 = 全部正确缩放。 */
  readonly skippedVectorLayers: number;
}

/**
 * 把整个文档缩放到新分辨率。返回是否执行、以及有多少图层是"已知限制"
 * 没跟着缩放（见文件头）。无文档/尺寸非法/尺寸未变时 `resized` 为 false。
 */
export async function resizeImage(
  store: AppStore,
  width: number,
  height: number,
  options: BakeOptions = {},
): Promise<ResizeImageResult> {
  const document = store.document;
  if (document === null) return { resized: false, skippedVectorLayers: 0 };
  const newWidth = Math.round(width);
  const newHeight = Math.round(height);
  if (newWidth < 1 || newHeight < 1) return { resized: false, skippedVectorLayers: 0 };
  if (newWidth === document.size.width && newHeight === document.size.height) {
    return { resized: false, skippedVectorLayers: 0 };
  }

  const scaleX = newWidth / document.size.width;
  const scaleY = newHeight / document.size.height;
  const matrix = scaling(scaleX, scaleY);

  const transaction = store.beginPixelTransaction('图像大小');
  const dirty: Array<{ tx: number; ty: number }> = [];
  const skippedVectorLayers = await resizeLayers(
    store.tiles,
    document.root.children,
    matrix,
    newWidth,
    newHeight,
    transaction,
    dirty,
    options,
  );

  store.setSelection(null); // 选区坐标随缩放失义，直接取消（跟裁剪/画布大小同一个理由）
  store.dispatchWithPixels(createSetCanvasSizeCommand(newWidth, newHeight), transaction.end());
  store.invalidatePixels(dirty);
  return { resized: true, skippedVectorLayers };
}
